import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

const MobileThemeContext = createContext();

export const useMobileTheme = () => {
  const context = useContext(MobileThemeContext);
  if (!context) {
    throw new Error('useMobileTheme must be used within a MobileThemeProvider');
  }
  return context;
};

export const MobileThemeProvider = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Получаем роль пользователя из Redux
  const authState = useSelector((state) => state.authSlice) || {};
  const userRole = authState.role;
  
  // Состояние темы для мобильных и десктопных пользователей (не админов)
  const [mobileTheme, setMobileTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('mobileTheme');
      return saved || 'white';
    }
    return 'white';
  });

  // Сохраняем тему в localStorage для мобильных и десктопных пользователей
  useEffect(() => {
    if (isMobile || userRole === 'user') {
      localStorage.setItem('mobileTheme', mobileTheme);
    }
  }, [mobileTheme, isMobile, userRole]);

  // Определяем цвета для каждой темы
  const getThemeColors = () => {
    // Проверяем, может ли пользователь переключать темы (мобильные или десктопные пользователи, не админы)
    const canSwitchThemes = isMobile || userRole === 'user';
    
    if (!canSwitchThemes) {
      // Для десктопных админов возвращаем только темную тему
      return {
        backgroundColor: '#071124',
        cardBackground: '#162238',
        textPrimary: '#ffffff',
        textSecondary: '#90a4ae',
        borderColor: '#2c3e50',
        hoverBackground: 'rgba(255, 255, 255, 0.05)'
      };
    }

    // Для мобильных устройств и десктопных пользователей
    if (mobileTheme === 'white') {
      return {
        backgroundColor: '#f8fafc',
        cardBackground: '#ffffff',
        textPrimary: '#000000', // Конкретно черный для светлой темы
        textSecondary: '#64748b',
        borderColor: '#e2e8f0',
        hoverBackground: 'rgba(59, 130, 246, 0.08)',
        accent: '#3b82f6',
        accentHover: '#2563eb',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#06b6d4'
      };
    } else {
      return {
        backgroundColor: '#071124',
        cardBackground: '#162238',
        textPrimary: '#ffffff', // Конкретно белый для темной темы
        textSecondary: '#90a4ae',
        borderColor: '#2c3e50',
        hoverBackground: 'rgba(255, 255, 255, 0.05)',
        accent: '#3b82f6',
        accentHover: '#2563eb',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#06b6d4'
      };
    }
  };

  const toggleMobileTheme = () => {
    // Разрешаем переключение темы для мобильных и десктопных пользователей (не админов)
    if (isMobile || userRole === 'user') {
      setMobileTheme(prev => prev === 'white' ? 'dark' : 'white');
    }
  };

  const value = {
    mobileTheme,
    isMobile,
    toggleMobileTheme,
    themeColors: getThemeColors(),
    isMobileDark: isMobile && mobileTheme === 'dark',
    isMobileWhite: isMobile && mobileTheme === 'white',
    canSwitchThemes: isMobile || userRole === 'user', // Новое свойство для проверки
    userRole
  };

  return (
    <MobileThemeContext.Provider value={value}>
      {children}
    </MobileThemeContext.Provider>
  );
};

MobileThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}; 